import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Gauge, AlertCircle } from "lucide-react";

function ScoreBlock({ label, value }) {
  return (
    <div className="border border-border p-3" data-testid={`audit-score-${label.toLowerCase()}`}>
      <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
      <p className="font-heading text-2xl font-black tracking-tighter">{value}</p>
    </div>
  );
}

export function SiteAuditDialog({ placeId, disabled }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    setAudit(null);
    try {
      const res = await api.post(`/prospects/${placeId}/audit-site`);
      setAudit(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'audit du site");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      runAudit();
    } else {
      setAudit(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size="icon"
        className="rounded-none"
        disabled={disabled}
        data-testid={`audit-site-button-${placeId}`}
        onClick={() => setOpen(true)}
      >
        <Gauge size={14} />
      </Button>
      <DialogContent className="rounded-none max-w-2xl" data-testid="site-audit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight">Audit du site</DialogTitle>
          <DialogDescription className="font-mono text-xs">
            Analyse automatique de la qualite et de l'opportunite commerciale du site
          </DialogDescription>
        </DialogHeader>
        {loading && (
          <p className="font-mono text-xs uppercase text-muted-foreground py-6 text-center" data-testid="audit-loading">
            Analyse en cours...
          </p>
        )}
        {!loading && audit && (
          <div className="space-y-4" data-testid="audit-results">
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-border p-4 col-span-1" data-testid="audit-quality-score">
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Website Quality Score</p>
                <p className="font-heading text-4xl font-black tracking-tighter">{audit.website_quality_score}/100</p>
              </div>
              <div className="border border-primary p-4 col-span-1" data-testid="audit-opportunity-score">
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Opportunity Score</p>
                <p className="font-heading text-4xl font-black tracking-tighter text-primary">{audit.opportunity_score}/100</p>
              </div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              <ScoreBlock label="Technique" value={audit.technical_score} />
              <ScoreBlock label="Mobile" value={audit.mobile_score} />
              <ScoreBlock label="SEO" value={audit.seo_score} />
              <ScoreBlock label="Contenu" value={audit.content_score} />
              <ScoreBlock label="Conversion" value={audit.conversion_score} />
              <ScoreBlock label="Confiance" value={audit.trust_score} />
            </div>
            {audit.problemes?.length > 0 && (
              <div data-testid="audit-problems-list">
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Problemes detectes</p>
                <ul className="space-y-1">
                  {audit.problemes.map((p, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <AlertCircle size={14} className="mt-0.5 shrink-0 text-[hsl(var(--warning))]" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {audit.erreurs?.length > 0 && (
              <p className="font-mono text-xs text-muted-foreground border-t border-border pt-3" data-testid="audit-errors">
                {audit.erreurs.join(" | ")}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
